/**
 * @lines98/client/components/toggle-switch — reusable ON/OFF toggle control.
 *
 * ОБЩИЙ модуль-контрол (owner-directive «код через модули»): любой настроечный
 * тумблер использует ЭТОТ компонент, не инлайнит свою кнопку. Сейчас потребитель —
 * `components/setting-row` (строка настройки на странице настроек, brief-39 §C).
 *
 * Доступность: нативный `<button role="switch" aria-checked>` (не `<input
 * type=checkbox>` — у него своя браузерная отрисовка, которую пришлось бы перебивать;
 * button + role=switch управляется CSS-токенами проекта и читается скринридером как
 * переключатель). Клавиатура: Space/Enter тогглят (нативное поведение `<button>`).
 *
 * Размеры — формулами от :root (`--btn-h` track-height, derived gutter/thumb); НЕ
 * magic-числа. Анимация — transform + background (GPU-friendly, не box-shadow —
 * skill ui-performance); `prefers-reduced-motion: reduce` отключает в CSS.
 *
 * CSS — co-located `toggle-switch.css`.
 */

import './toggle-switch.css';

export interface ToggleSwitchOptions {
  /** Начальное состояние. */
  readonly checked: boolean;
  /** Accessible label (aria-label) — описывает, чем управляет тумблер. */
  readonly ariaLabel: string;
  /** Вызывается на каждое переключение пользователем с новым состоянием. */
  readonly onChange: (checked: boolean) => void;
  /**
   * Неактивный (плейсхолдер «скоро» — напр. режим для дальтоников, §5.5): кнопка
   * `disabled` + recessive-вид (CSS `.toggle-switch--disabled`); клики не меняют
   * состояние и не зовут onChange.
   */
  readonly disabled?: boolean;
}

export interface ToggleSwitchHandle {
  /** Корневой элемент (button) — потребитель вставляет его в свой контейнер. */
  readonly element: HTMLButtonElement;
  /** Текущее состояние. */
  isChecked(): boolean;
  /** Программно задать состояние БЕЗ вызова onChange (для внешней синхронизации). */
  setChecked(checked: boolean): void;
}

/**
 * Создать toggle-switch. Возвращает handle с `.element` для монтирования.
 */
export function createToggleSwitch(opts: ToggleSwitchOptions): ToggleSwitchHandle {
  let checked = opts.checked;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'toggle-switch';
  btn.setAttribute('role', 'switch');
  btn.setAttribute('aria-label', opts.ariaLabel);
  btn.setAttribute('aria-checked', String(checked));

  const thumb = document.createElement('span');
  thumb.className = 'toggle-switch-thumb';
  thumb.setAttribute('aria-hidden', 'true');
  btn.appendChild(thumb);

  const disabled = !!opts.disabled;
  if (disabled) {
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('toggle-switch--disabled');
  }

  const applyState = (): void => {
    btn.setAttribute('aria-checked', String(checked));
    btn.classList.toggle('toggle-switch--on', checked);
  };
  applyState();

  btn.addEventListener('click', () => {
    if (disabled) return;
    checked = !checked;
    applyState();
    opts.onChange(checked);
  });

  return {
    element: btn,
    isChecked: () => checked,
    setChecked(next: boolean): void {
      if (next === checked) return;
      checked = next;
      applyState();
    },
  };
}
